/**
 * Grilla de mesas.
 *
 * Es lo primero que mira el mesero al entrar, así que cada tarjeta responde
 * tres preguntas de un vistazo: si la mesa está ocupada, hace cuánto, y
 * cuánto lleva consumido. El color hace el trabajo pesado —punteada libre,
 * lila ocupada, ámbar cuenta pedida— y el punto de la esquina refuerza el
 * estado para quien no distinga bien los tonos.
 */

import { cop, pad2 } from '../lib/formato';
import type { MesaVista } from '../datos/vistas';

type Filtro = 'todas' | 'libre' | 'ocupada' | 'cuenta';

interface Props {
  mesas: MesaVista[] | undefined;
  seleccionada: string | null;
  filtro: Filtro;
  onFiltro: (f: Filtro) => void;
  onSeleccionar: (mesa: MesaVista) => void;
  onNuevoPuesto: () => void;
}

export function PanelMesas({
  mesas,
  seleccionada,
  filtro,
  onFiltro,
  onSeleccionar,
  onNuevoPuesto,
}: Props) {
  const visibles = (mesas ?? []).filter((m) => filtro === 'todas' || m.estado === filtro);

  return (
    <section className="card tables" style={{ flex: 1 }}>
      <header>
        <div className="eyebrow">Salón</div>
        <button className="newtable" onClick={onNuevoPuesto}>
          + Puesto
        </button>
      </header>

      <select
        className="filter"
        value={filtro}
        onChange={(e) => onFiltro(e.target.value as Filtro)}
        aria-label="Filtrar mesas"
      >
        <option value="todas">Todas las mesas</option>
        <option value="libre">Libres</option>
        <option value="ocupada">Ocupadas</option>
        <option value="cuenta">Cuenta pedida</option>
      </select>

      <div className="grid">
        {mesas === undefined && <div className="none">Cargando salón…</div>}

        {mesas !== undefined && visibles.length === 0 && (
          <div className="none" style={{ gridColumn: '1 / -1' }}>
            No hay mesas en este filtro.
          </div>
        )}

        {visibles.map((m) => {
          const clases = [
            'table-btn',
            m.estado,
            m.id === seleccionada && m.estado !== 'libre' ? 'sel' : '',
          ]
            .filter(Boolean)
            .join(' ');

          // Con varias cuentas, lo que importa de un vistazo es cuántas hay y
          // cuánto suman; los minutos pasan a segundo plano.
          const sub =
            m.estado === 'libre'
              ? `${m.capacidad} puestos`
              : m.cuentas.length > 1
                ? `${m.cuentas.length} cuentas · ${cop(m.total)}`
                : m.estado === 'cuenta'
                  ? `Cuenta pedida · ${cop(m.total)}`
                  : `${m.minutos} min · ${cop(m.total)}`;

          return (
            <button
              key={m.id}
              className={clases}
              onClick={() => onSeleccionar(m)}
              title={
                m.cuentas.length > 1
                  ? `${m.nombre ?? `Mesa ${m.numero}`} · ${m.cuentas.length} cuentas abiertas`
                  : (m.nombre ?? `Mesa ${m.numero}`)
              }
            >
              <span className="dot" />
              {m.cuentas.length > 1 && <span className="cuentas mono">{m.cuentas.length}</span>}
              <span className="num mono">{m.esTemporal ? m.numero : pad2(m.numero)}</span>
              <span className="sub">{sub}</span>
              {m.esTemporal && m.nombre && <span className="sub puesto">{m.nombre}</span>}
            </button>
          );
        })}
      </div>

      <div className="legend">
        <span>
          <i style={{ background: 'var(--line-strong)' }} />
          Libre
        </span>
        <span>
          <i style={{ background: 'var(--primary)' }} />
          Ocupada
        </span>
        <span>
          <i style={{ background: 'var(--accent-400)' }} />
          Cuenta pedida
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
          {(mesas ?? []).filter((m) => m.estado !== 'libre').length}/{(mesas ?? []).length}
        </span>
      </div>
    </section>
  );
}
